import * as Dialog from '@radix-ui/react-dialog'
import { useState } from 'react'
import { match } from 'ts-pattern'
import { DefaultButton } from '../../components/buttons/default-button.tsx'
import AzureLightIcon from '../../components/icons/azure-light.svg?react'
import GitLabIcon from '../../components/icons/gitlab-icon.svg?react'
import { SelectBox } from '../../components/input/select-box.tsx'
import { TextInput } from '../../components/input/text-input.tsx'
import { AzureOrganization, AzureProject, AzureRepository, GitlabProject } from '../../utils/api-client/api-schemas'
import { EnvironmentName } from '../../utils/hooks/get-environment-data.ts'
import { HandleConnectAzureRepository, HandleConnectGitRepository } from '../repositories-page.tsx'
import * as styles from './add-repository-modal.css'
import SelectedRadioSVG from './selected-radio.svg?react'
import UnselectedRadioSVG from './unselected-radio.svg?react'

export interface GitHubIntegrationProps {
  githubAppName: string
}

export interface AzureIntegrationProps {
  azureOrganizations: AzureOrganization[]
  azureProjects: AzureProject[]
  azureRepositories: AzureRepository[]
  selectedAzureOrganizationId: string | undefined
  selectedAzureProjectId: string | undefined
  selectedAzureRepositoryId: string | undefined
  handleChangeAzureOrganizationId: (value: string) => void
  handleChangeAzureProjectId: (value: string) => void
  handleChangeAzureRepositoryId: React.Dispatch<React.SetStateAction<string | undefined>>
  handleConnectAzureRepository: HandleConnectAzureRepository
}

export interface GitlabIntegrationProps {
  gitlabProjects: GitlabProject[]
  selectedGitLabProjectId: string | undefined
  handleChangeGitLabProjectId: (value: string) => void
  handleConnectGitLabRepository: (params: { path: string; namespace_full_path: string }) => void
}

export interface AddRepositoryModalPureProps {
  environmentName: EnvironmentName
  onClose: () => void
  handleConnectGitRepository: HandleConnectGitRepository
  githubIntegrationProps?: GitHubIntegrationProps
  azureIntegrationProps?: AzureIntegrationProps
  gitlabIntegrationProps?: GitlabIntegrationProps
}

export type ConnectionType = 'GitHubApp' | 'GenericGit' | 'Integration'

export const AddRepositoryModalPure: React.FC<AddRepositoryModalPureProps> = ({
  environmentName,
  onClose,
  handleConnectGitRepository,
  githubIntegrationProps,
  azureIntegrationProps,
  gitlabIntegrationProps,
}) => {
  const [selectedConnectionType, setSelectedConnectionType] = useState<ConnectionType>('GitHubApp')

  const [username, setUsername] = useState('')
  const [password, setPassword] = useState('')
  const [url, setUrl] = useState('')

  return (
    <Dialog.Root open={true}>
      <Dialog.Portal>
        <Dialog.Overlay className={styles.dialogOverlay} />
        <Dialog.Content className={styles.dialogContent} aria-describedby={undefined} onEscapeKeyDown={onClose}>
          <Dialog.Title className={styles.title}>New repository</Dialog.Title>
          <form>
            <ConnectionTypeRadioInput
              environmentName={environmentName}
              selectedConnectionType={selectedConnectionType}
              setSelectedConnectionType={setSelectedConnectionType}
              isGitHubIntegrationEnabled={!!githubIntegrationProps}
              isAzureIntegrationEnabled={!!azureIntegrationProps}
              isGitlabIntegrationEnabled={!!gitlabIntegrationProps}
            />
            {match(selectedConnectionType)
              .with(
                'GitHubApp',
                () => githubIntegrationProps && <ConnectPixeebotApp onClose={onClose} githubIntegrationProps={githubIntegrationProps} />
              )
              .with('GenericGit', () => (
                <ConnectGenericGit
                  onClose={onClose}
                  url={url}
                  username={username}
                  password={password}
                  setUrl={setUrl}
                  setUsername={setUsername}
                  setPassword={setPassword}
                  handleConnectGitRepository={handleConnectGitRepository}
                />
              ))
              .with(
                'Integration',
                () =>
                  (azureIntegrationProps || gitlabIntegrationProps) && (
                    <ConnectIntegration
                      onClose={onClose}
                      azureIntegrationProps={azureIntegrationProps}
                      gitlabIntegrationProps={gitlabIntegrationProps}
                    />
                  )
              )
              .exhaustive()}
          </form>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  )
}

interface ConnectionTypeRadioInputProps {
  environmentName: EnvironmentName
  selectedConnectionType: ConnectionType
  setSelectedConnectionType: (type: ConnectionType) => void
  isGitHubIntegrationEnabled: boolean
  isAzureIntegrationEnabled: boolean
  isGitlabIntegrationEnabled: boolean
}

const ConnectionTypeRadioInput: React.FC<ConnectionTypeRadioInputProps> = ({
  environmentName,
  selectedConnectionType,
  setSelectedConnectionType,
  isGitHubIntegrationEnabled,
  isAzureIntegrationEnabled,
  isGitlabIntegrationEnabled,
}) => {
  return (
    <div className={styles.radioGroup}>
      {isGitHubIntegrationEnabled && (
        <label className={styles.radioButton}>
          <input
            type="radio"
            name="connectionType"
            value="GitHubApp"
            checked={selectedConnectionType === 'GitHubApp'}
            onChange={() => setSelectedConnectionType('GitHubApp')}
            className={styles.hiddenRadioButton}
          />
          {getRadioIcon(selectedConnectionType === 'GitHubApp')}
          Install the {environmentName === 'ntt' ? 'Github' : 'Pixeebot GitHub'} application (GitHub repositories only)
        </label>
      )}
      <label className={styles.radioButton}>
        <input
          type="radio"
          name="connectionType"
          value="GenericGit"
          checked={selectedConnectionType === 'GenericGit'}
          onChange={() => setSelectedConnectionType('GenericGit')}
          className={styles.hiddenRadioButton}
        />
        {getRadioIcon(selectedConnectionType === 'GenericGit')}
        Connect via Git client (clone a repository from any SCM)
      </label>
      {(isAzureIntegrationEnabled || isGitlabIntegrationEnabled) && (
        <label className={styles.radioButton}>
          <input
            type="radio"
            name="connectionType"
            value="Integration"
            checked={selectedConnectionType === 'Integration'}
            onChange={() => setSelectedConnectionType('Integration')}
            className={styles.hiddenRadioButton}
          />
          {getRadioIcon(selectedConnectionType === 'Integration')}
          Connect to repositories from an existing integration
        </label>
      )}
    </div>
  )
}

const getRadioIcon = (isSelected: boolean) => (isSelected ? <SelectedRadioSVG /> : <UnselectedRadioSVG />)

interface ConnectPixeebotAppProps {
  onClose: () => void
  githubIntegrationProps: GitHubIntegrationProps
}

const ConnectPixeebotApp: React.FC<ConnectPixeebotAppProps> = ({ onClose, githubIntegrationProps }) => {
  return (
    <div className={styles.buttonGroup}>
      <DefaultButton buttonType="secondary" onClick={onClose}>
        Cancel
      </DefaultButton>
      <DefaultButton
        href={`https://github.com/apps/${githubIntegrationProps.githubAppName}/installations/new`}
        buttonType="primary"
        onClick={onClose}
      >
        Go to GitHub
      </DefaultButton>
    </div>
  )
}

interface ConnectIntegrationProps {
  onClose: () => void
  azureIntegrationProps?: AzureIntegrationProps
  gitlabIntegrationProps?: GitlabIntegrationProps
}

type IntegrationType = 'azureDevOps' | 'gitlab'
const ConnectIntegration: React.FC<ConnectIntegrationProps> = ({ onClose, azureIntegrationProps, gitlabIntegrationProps }) => {
  if (!azureIntegrationProps && !gitlabIntegrationProps) {
    throw new Error('azureIntegrationProps and gitlabIntegrationProps are both undefined')
  }

  const [selectedIntegration, setSelectedIntegration] = useState<IntegrationType>(azureIntegrationProps ? 'azureDevOps' : 'gitlab')

  return (
    <>
      <div className={styles.inputGroup}>
        <SelectBox
          label="Integration"
          value={selectedIntegration}
          onValueChange={value => {
            setSelectedIntegration(value as IntegrationType)
          }}
          options={[
            {
              value: 'azureDevOps',
              label: (
                <span className={styles.selectItemText}>
                  <AzureLightIcon className={styles.scmIcon} />
                  Azure DevOps
                </span>
              ),
            },
            {
              value: 'gitlab',
              label: (
                <span className={styles.selectItemText}>
                  <GitLabIcon className={styles.scmIcon} />
                  GitLab
                </span>
              ),
            },
          ].filter(option => {
            if (option.value === 'gitlab' && !gitlabIntegrationProps) {
              return false
            }

            if (option.value === 'azureDevOps' && !azureIntegrationProps) {
              return false
            }

            return true
          })}
        />
        {match(selectedIntegration)
          .with('azureDevOps', () => {
            if (!azureIntegrationProps) {
              return null
            }

            const {
              azureOrganizations,
              azureProjects,
              azureRepositories,
              selectedAzureOrganizationId,
              selectedAzureProjectId,
              selectedAzureRepositoryId,
              handleChangeAzureOrganizationId,
              handleChangeAzureProjectId,
              handleChangeAzureRepositoryId,
            } = azureIntegrationProps

            const azureOrganizationOptions = azureOrganizations.map(org => ({
              value: org.name,
              label: org.name,
            }))
            const azureProjectOptions = azureProjects.map(project => ({
              value: project.id,
              label: project.name,
            }))
            const azureRepositoryOptions = azureRepositories.map(repo => ({
              value: repo.id,
              label: repo.name,
              disabled: repo.pixee_id !== null,
            }))

            return (
              <>
                <SelectBox
                  label="Organization"
                  value={selectedAzureOrganizationId}
                  onValueChange={handleChangeAzureOrganizationId}
                  placeholder="Select organization"
                  options={azureOrganizationOptions}
                />
                <SelectBox
                  label="Project"
                  value={selectedAzureProjectId}
                  onValueChange={handleChangeAzureProjectId}
                  disabled={!selectedAzureOrganizationId}
                  placeholder="Select project"
                  options={azureProjectOptions}
                />
                <SelectBox
                  label="Available repositories"
                  value={selectedAzureRepositoryId}
                  onValueChange={handleChangeAzureRepositoryId}
                  disabled={!selectedAzureProjectId}
                  placeholder="Select repository"
                  options={azureRepositoryOptions}
                />
              </>
            )
          })
          .with('gitlab', () => {
            if (!gitlabIntegrationProps) {
              return null
            }

            const { gitlabProjects, selectedGitLabProjectId, handleChangeGitLabProjectId } = gitlabIntegrationProps

            return (
              <SelectBox
                label="Available projects"
                value={selectedGitLabProjectId}
                onValueChange={handleChangeGitLabProjectId}
                options={gitlabProjects.map(project => ({
                  value: project.path,
                  label: project.name_with_namespace,
                }))}
              />
            )
          })
          .exhaustive()}
      </div>
      <div className={styles.buttonGroup}>
        <DefaultButton buttonType="secondary" onClick={onClose}>
          Cancel
        </DefaultButton>
        {match(selectedIntegration)
          .with('azureDevOps', () => {
            if (!azureIntegrationProps) {
              return null
            }

            const {
              azureRepositories,
              azureProjects,
              selectedAzureOrganizationId,
              selectedAzureProjectId,
              selectedAzureRepositoryId,
              handleConnectAzureRepository,
            } = azureIntegrationProps

            const selectedAzureRepository = azureRepositories.find(repo => repo.id === selectedAzureRepositoryId)
            const selectedAzureProject = azureProjects.find(proj => proj.id === selectedAzureProjectId)

            return (
              <DefaultButton
                buttonType="primary"
                state={selectedAzureRepositoryId ? 'default' : 'disabled'}
                onClick={() => {
                  if (selectedAzureOrganizationId && selectedAzureProject && selectedAzureRepository) {
                    handleConnectAzureRepository({
                      selectedAzureOrganizationId,
                      selectedAzureProjectName: selectedAzureProject.name,
                      selectedAzureRepositoryName: selectedAzureRepository.name,
                    })
                  }
                }}
              >
                Connect
              </DefaultButton>
            )
          })
          .with('gitlab', () => {
            if (!gitlabIntegrationProps) {
              return null
            }

            const { gitlabProjects, selectedGitLabProjectId, handleConnectGitLabRepository } = gitlabIntegrationProps
            const selectedGitLabProject = gitlabProjects.find(project => project.path === selectedGitLabProjectId)

            return (
              <DefaultButton
                buttonType="primary"
                state={selectedGitLabProjectId ? 'default' : 'disabled'}
                onClick={() => {
                  if (selectedGitLabProject) {
                    handleConnectGitLabRepository({
                      path: selectedGitLabProject.path,
                      namespace_full_path: selectedGitLabProject.namespace_full_path,
                    })
                  }
                }}
              >
                Connect
              </DefaultButton>
            )
          })
          .exhaustive()}
      </div>
    </>
  )
}

interface ConnectGitClientProps {
  onClose: () => void
  url: string
  username: string
  password: string
  setUrl: (value: string) => void
  setUsername: (value: string) => void
  setPassword: (value: string) => void
  handleConnectGitRepository: HandleConnectGitRepository
}

const ConnectGenericGit: React.FC<ConnectGitClientProps> = ({
  onClose,
  url,
  username,
  password,
  setUrl,
  setUsername,
  setPassword,
  handleConnectGitRepository,
}) => {
  return (
    <>
      <div className={styles.inputGroup}>
        <TextInput
          label="URL"
          id="repository-url"
          name="url"
          placeholder="ex: https://scm.com/org/repo-lang.git"
          value={url}
          onChange={value => setUrl(value.toString())}
          isRequired
        />
        <TextInput
          label="Username"
          id="repository-username"
          name="username"
          placeholder="Enter username"
          value={username}
          onChange={value => setUsername(value.toString())}
          isOptional
        />
        <TextInput
          type="password"
          label="Password"
          id="repository-password"
          name="password"
          placeholder="Enter password"
          value={password}
          onChange={value => setPassword(value.toString())}
          isOptional
        />
      </div>
      <div className={styles.buttonGroup}>
        <DefaultButton buttonType="secondary" onClick={onClose}>
          Cancel
        </DefaultButton>
        <DefaultButton
          buttonType="primary"
          state={url ? 'default' : 'disabled'}
          onClick={() =>
            handleConnectGitRepository({
              url,
              username,
              password,
              onSuccess: () => {
                setUrl('')
                setUsername('')
                setPassword('')
              },
            })
          }
        >
          Connect
        </DefaultButton>
      </div>
    </>
  )
}
